import { createContext, useContext, useEffect } from 'react';
import { useAIDrawer } from '../hooks/useAIDrawer';
import type { OsWindowVariant } from './osRouteMeta';

type OsWindowVariantSetter = (variant: OsWindowVariant | null) => void;

export const OsWindowVariantOverrideContext =
  createContext<OsWindowVariantSetter | null>(null);

/**
 * Lets a page override its route-declared window variant while `active` —
 * e.g. a game round forcing fullscreen. Cleared automatically when the
 * component unmounts or `active` goes false. The provider is mounted at every
 * viewport width, so the override applies (harmlessly) below the breakpoint
 * (no shell, no provider).
 *
 * A page that takes the whole screen takes the companion with it: its
 * floating bubble would sit on top of a timed round, and the round is not a
 * surface to ask questions about. The route-declared fullscreen surfaces
 * (study) are unaffected — they keep the push drawer, which is part of their
 * layout. This runs at every width, so it also covers the mobile sheet.
 *
 * It also forces the dark theme for the duration: a round is a branded dark
 * surface whatever the student's theme, while the screens around it (hub,
 * start, results) follow the theme. The `forceDarkTheme` body flag tells
 * App's theme effect to leave `theme-dark` alone meanwhile, and a dark
 * student's own class is put back untouched when the round ends.
 */
export const useOsWindowVariantOverride = (
  variant: OsWindowVariant,
  active: boolean
) => {
  const setOverride = useContext(OsWindowVariantOverrideContext);
  const { setIsPageSuppressed } = useAIDrawer();
  const isFullscreen = variant === 'fullscreen';

  useEffect(() => {
    if (!setOverride || !active) return;

    setOverride(variant);

    return () => setOverride(null);
  }, [setOverride, variant, active]);

  useEffect(() => {
    if (!isFullscreen || !active) return;

    setIsPageSuppressed(true);

    return () => setIsPageSuppressed(false);
  }, [active, isFullscreen, setIsPageSuppressed]);

  useEffect(() => {
    if (!isFullscreen || !active) return;

    const { body } = document;
    const hadDarkTheme = body.classList.contains('theme-dark');
    body.classList.add('theme-dark');
    body.dataset.forceDarkTheme = 'true';

    return () => {
      delete body.dataset.forceDarkTheme;
      if (!hadDarkTheme) body.classList.remove('theme-dark');
    };
  }, [active, isFullscreen]);
};
